import type { ChangeEvent, FormEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { saveQuickReply } from '@/msgdeskRuntime/legacyBridge/legacyActions'
import { classNames, legacyContractDataAttrs, legacyCssClasses, legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { getMsgDeskRuntime } from '@/msgdeskRuntime'
import { useSharedSurface } from './SharedSurfaceContext'

type QuickReplyRequest = {
  elId: number
  isCompact: boolean
  emailText: string
  canSendEmail: boolean
  isSendEmail: boolean
}

const quickReplyListeners = new Set<() => void>()
let currentQuickReply: QuickReplyRequest | null = null

function emitQuickReplyChange() {
  for (const listener of quickReplyListeners) listener()
}

function subscribeQuickReply(listener: () => void) {
  quickReplyListeners.add(listener)
  return () => {
    quickReplyListeners.delete(listener)
  }
}

function readQuickReplyMsgData(elId: number) {
  return getMsgDeskRuntime().facade.msgData(elId) || null
}

export function showQuickReplyModal(elId = 0, isCompact = false) {
  const msgData = readQuickReplyMsgData(elId) || {}
  const emailFrom = typeof msgData.email_from === 'string' && msgData.email_from !== '0' ? msgData.email_from : ''
  const emailTo = typeof msgData.email_to === 'string' && msgData.email_to !== '0' ? msgData.email_to : ''
  currentQuickReply = {
    elId,
    isCompact,
    emailText: emailFrom || emailTo,
    canSendEmail: Boolean(emailFrom || emailTo),
    isSendEmail: Boolean(emailFrom),
  }
  emitQuickReplyChange()
}

export function closeQuickReplyModal() {
  currentQuickReply = null
  emitQuickReplyChange()
}

/** QR-IN-PLACE: subscribe to the open quick-reply target (null when closed).
 * The row host uses it to mount the inline composer under the replied row. */
export function useQuickReplyTarget(): QuickReplyRequest | null {
  const [dialog, setDialog] = useState<QuickReplyRequest | null>(currentQuickReply)
  useEffect(() => subscribeQuickReply(() => setDialog(currentQuickReply)), [])
  return dialog
}

/**
 * QR-IN-PLACE: the composer form itself — legacy contracts, skin tokens, and
 * the rough[2] save path — independent of WHERE it renders: the row host
 * mounts it inline under a row; QuickReplyModal hosts the main-msg (elId 0)
 * instance as a panel (or the legacy overlay fallback).
 */
export function QuickReplyComposer({ dialog }: { dialog: QuickReplyRequest }) {
  const { skin } = useSharedSurface()
  const [text, setText] = useState('')
  const [sendEmail, setSendEmail] = useState(false)
  const [isLinkOnly, setIsLinkOnly] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    setText('')
    setSendEmail(dialog.isSendEmail || false)
    setIsLinkOnly(false)
    textareaRef.current?.focus()
  }, [dialog.elId, dialog.isCompact])

  const activeDialog = dialog

  function syncLegacyQuickReplyState() {
    const toggleQr = getMsgDeskRuntime().facade.quickReplyToggleControl()
    // The reply TEXT is React-owned. Hand it to the legacy save through the
    // rough tuple (elId/isCompact already ride here) — legacy _qr_save reads
    // rough[2] instead of the legacy qr_text_area DOM element, which the React
    // shim cannot resolve to this React textarea. Save + tree projection stay
    // legacy-owned (insert_new_subs); only the text source moves to React.
    if (toggleQr) toggleQr.rough = [activeDialog.elId, activeDialog.isCompact, text]
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!text.trim()) {
      closeQuickReplyModal()
      return
    }
    syncLegacyQuickReplyState()
    saveQuickReply()
    closeQuickReplyModal()
  }

  function handleQrSendEmailChange(event: ChangeEvent<HTMLInputElement>) {
    const next = event.currentTarget.checked
    setSendEmail(next)
    if (!next) setIsLinkOnly(false)
  }

  function handleQrLinkOnlyChange(event: ChangeEvent<HTMLInputElement>) {
    setIsLinkOnly(event.currentTarget.checked)
  }

  return (
    <div {...legacyHookAttrs('editor.quickReply.root', classNames(`md-quick-reply ${skin.qrRoot}`, legacyCssClasses('quickReply.root')))}>
        <div className={classNames(`md-quick-reply__form ${skin.qrForm}`, legacyCssClasses('quickReply.form'))}>
          <div className={classNames(`md-quick-reply__edit-form ${skin.qrEditForm}`, legacyCssClasses('quickReply.editForm'))}>
            <form {...legacyContractDataAttrs('editor.quickReply.form')} onSubmit={handleSubmit}>
              <textarea
                {...legacyContractDataAttrs('editor.quickReply.input')}
                ref={textareaRef}
                placeholder="Your quick reply ..."
                className={classNames(`md-quick-reply__textarea ${skin.qrTextarea}`, legacyCssClasses('quickReply.textarea'))}
                name="txt"
                wrap="virtual"
                rows={7}
                value={text}
                onChange={event => setText(event.currentTarget.value)}
              />
              {activeDialog.canSendEmail ? (
                <label className={classNames(`md-quick-reply__email-label ${skin.qrEmailLabel}`, legacyCssClasses('quickReply.emailLabel'))}>
                  <input
                    {...legacyContractDataAttrs('editor.quickReply.sendEmail')}
                    onChange={handleQrSendEmailChange}
                    type="checkbox"
                    className={classNames(`md-quick-reply__checkbox ${skin.qrCheckbox}`, legacyCssClasses('quickReply.checkbox'))}
                    value="1"
                    checked={sendEmail}
                  />
                  <span className={classNames(`md-quick-reply__email-link ${skin.qrEmailLink}`, legacyCssClasses('quickReply.emailLink'))}>
                    send to <span className={skin.qrEmailValue}>{activeDialog.emailText}</span>
                  </span>
                </label>
              ) : null}
              <label className={classNames(`md-quick-reply__email-label ${skin.qrEmailLabel}`, legacyCssClasses('quickReply.emailLabel'))} style={{ display: sendEmail ? undefined : 'none' }}>
                <input
                  {...legacyContractDataAttrs('editor.quickReply.link')}
                  onChange={handleQrLinkOnlyChange}
                  type="checkbox"
                  className={classNames(`md-quick-reply__checkbox ${skin.qrCheckbox}`, legacyCssClasses('quickReply.checkbox'))}
                  value="1"
                  checked={isLinkOnly}
                />
                <span className={classNames(`md-quick-reply__email-link ${skin.qrEmailLink}`, legacyCssClasses('quickReply.emailLink'))}>Link Only</span>
              </label>
              <div className={`md-quick-reply__actions ${skin.qrActions}`}>
                <button type="submit" className={classNames(`md-quick-reply__save-button ${skin.qrSaveButton}`, legacyCssClasses('quickReply.saveButton'))}>
                  Reply
                </button>
                <button type="button" className={classNames(`md-quick-reply__save-button ${skin.qrSaveButton}`, legacyCssClasses('quickReply.saveButton'))} onClick={closeQuickReplyModal}>
                  Close
                </button>
              </div>
            </form>
          </div>
          <div className={classNames(`md-quick-reply__shadow-right ${skin.qrShadowRight}`, legacyCssClasses('quickReply.shadowRight', 'quickReply.opacity'))} />
        </div>
        <div className={classNames(`md-quick-reply__shadow-bottom ${skin.qrShadowBottom}`, legacyCssClasses('quickReply.shadowBottom', 'quickReply.opacity'))} />
      </div>
  )
}

/**
 * The once-per-surface mount (Overlays.QuickReply). QR-IN-PLACE: it now
 * hosts ONLY the MAIN-msg composer (elId 0, the menu Reply control) — row
 * composers are mounted inline by the row host. With a qrMainPanel skin
 * token the main composer is an anchored panel; without one it falls back
 * to the legacy centered overlay.
 */
export function QuickReplyModal() {
  const { skin } = useSharedSurface()
  const dialog = useQuickReplyTarget()

  if (!dialog || dialog.elId !== 0) return null

  if (skin.qrMainPanel) {
    return (
      <div className={`md-quick-reply-panel ${skin.qrMainPanel}`} role="dialog" aria-label="Quick reply">
        <QuickReplyComposer dialog={dialog} />
      </div>
    )
  }

  return (
    <div className={`md-quick-reply-modal ${skin.qrOverlay}`} role="dialog" aria-modal="true" aria-label="Quick reply">
      <QuickReplyComposer dialog={dialog} />
    </div>
  )
}
